import { create } from 'zustand';
import { todayISO } from '../utils/formatters';
import eventsService from '../api/services/events.service';

let nextEventId = 1;

/* Event modes per the redesign: 'invite' (parents RSVP), 'noRsvp' (announcement,
   parents can come), 'studentsOnly' (no parents — no email ever). Saving never
   sends; each row sends now or schedules from the calendar. */
const useEventsStore = create((set, get) => ({
  events: [],
  eventsLoading: false,
  /* Which network the loaded list belongs to — null until the first load
     finishes. Lets a page tell "not fetched yet" (spinner) apart from "nothing
     scheduled" (empty state), and keeps one class's events from showing up on
     another's screen while the switch is in flight. */
  eventsNetworkId: null,
  eventsTotal: 0,
  eventsTotalPages: 1,

  /* Days off on the same page as the events — the NoSchool notes the list
     unions in (a day off set on the Master Calendar reaches every class under
     the school). Kept apart from `events` so everything else reading this
     store (dashboard counts and upcoming rows, donation and expense tags,
     the email's event picks) stays about events; the calendar page merges
     the two for display. */
  noSchoolDays: [],

  removeNoSchoolDay: (id) => set({ noSchoolDays: get().noSchoolDays.filter((d) => d.id !== id) }),

  /* Server-paginated events for the selected network — the only source, so
     everything reading this store (calendar, dashboard, donation-reason chips)
     sees the class's real events. A failed load leaves nothing behind rather
     than the previous network's events; callers surface the error. */
  loadEvents: async (networkId, { page = 1, limit = 10 } = {}, scope = 'class') => {
    if (!networkId) return;
    set({ eventsLoading: true });
    try {
      /* only a school's calendar carries days off; a class or sub group lists plain events */
      const r = await eventsService.listEvents(networkId, { page, limit, withNoSchool: scope === 'master' });
      set({
        events: r.events.map((e) => ({ sendDate: null, sent: e.isEmailSent, scope, mode: e.eventType || 'invite', ...e })),
        noSchoolDays: r.noSchoolDays,
        eventsTotal: r.total,
        eventsTotalPages: r.totalPages,
      });
    } catch (err) {
      set({ events: [], noSchoolDays: [], eventsTotal: 0, eventsTotalPages: 1 });
      throw err;
    } finally {
      set({ eventsLoading: false, eventsNetworkId: networkId });
    }
  },

  eventById: (id) => get().events.find((e) => e.id === id) || null,

  addEvent: (event) => {
    const id = nextEventId++;
    set({ events: [...get().events, { id, sendDate: null, sent: false, ...event }] });
    return id;
  },

  updateEvent: (id, patch) =>
    set({ events: get().events.map((e) => (e.id === id ? { ...e, ...patch } : e)) }),

  /* Send now (sendDate null) or schedule (sendDate future) — one door for both. */
  confirmInviteSend: (id, { subject, sendDate }) =>
    set({
      events: get().events.map((e) =>
        e.id === id ? { ...e, inviteSubject: subject, sendDate: sendDate || null, sent: !sendDate } : e
      ),
    }),

  markEventsSent: (ids) =>
    set({ events: get().events.map((e) => (ids.includes(e.id) ? { ...e, sent: true } : e)) }),

  /* A scheduled email fires by itself once its date arrives — checked on every
     dashboard/calendar render, nobody has to come back and click anything. */
  processScheduledSends: () => {
    const today = todayISO();
    if (!get().events.some((e) => !e.sent && e.sendDate && e.sendDate <= today)) return;
    set({
      events: get().events.map((e) =>
        !e.sent && e.sendDate && e.sendDate <= today ? { ...e, sent: true } : e
      ),
    });
  },

  deleteEvent: (id) => set({ events: get().events.filter((e) => e.id !== id) }),

  setIncludeInEmail: (id, includeInEmail) =>
    set({ events: get().events.map((e) => (e.id === id ? { ...e, includeInEmail } : e)) }),
}));

/* Every event needs a legible state — exactly one of: Students only / Sent /
   Scheduled (with date) / Not sent yet. */
export const eventStatusChip = (ev, fmtDate) => {
  if (ev.mode === 'studentsOnly') return { cls: 'draft', text: 'Students only' };
  if (ev.sent) return { cls: 'published', text: ev.mode === 'noRsvp' ? 'Announcement sent' : 'Invitation sent' };
  if (ev.sendDate) return { cls: 'draft', text: `Scheduled — Email sends ${fmtDate(ev.sendDate)}` };
  return { cls: 'draft', text: 'Not sent yet' };
};

export const eventRsvpCounts = (ev) => {
  const rsvps = ev.rsvps || [];
  return {
    yes: rsvps.filter((r) => r.response === 'yes').length,
    no: rsvps.filter((r) => r.response === 'no').length,
  };
};

export default useEventsStore;
